// Data preparation helpers for the Player Value tab (market-value history).
// Pure functions, mirrored 1:1 from the design handoff (ValueTab.dc.html):
// formatting, nice-step axis logic, range filtering, summary + table rows.
// API contract: GET /dashboard/players/public/{id}/market-value/
//   { updated_at: <unix seconds|null>, history: [{ time, value, currency,
//     team_id, team_name, team_logo, age }] } — history ascending by time,
//   team_name/team_logo/age may be null.

const SYMBOLS = { EUR: "€", GBP: "£", USD: "$", BRL: "R$" };

// The payload's currency field can be an ISO code ("EUR") OR already a symbol
// ("€", "R$") — the DB row stores whatever the provider sent. Known codes map
// to their symbol, a value that is not purely alphabetic is already a symbol
// and is used verbatim, unknown alphabetic codes render as "CODE " prefix.
export const currencySymbol = (code) => {
  if (!code) return "€";
  if (SYMBOLS[code]) return SYMBOLS[code];
  if (!/^[A-Za-z]+$/.test(code)) return code;
  return code + " ";
};

// €45.0m / €900k / €250 — never a hardcoded €, symbol comes from the payload.
export const formatValue = (v, code) => {
  const s = currencySymbol(code);
  if (v >= 1000000) return s + (v / 1000000).toFixed(1) + "m";
  if (v >= 1000) return s + Math.round(v / 1000) + "k";
  return s + String(v);
};

// Axis ticks drop the decimal once values reach two-digit millions.
export const formatTick = (v, code) => {
  const s = currencySymbol(code);
  if (v >= 1000000)
    return (
      s + (v / 1000000 >= 10 ? Math.round(v / 1000000) : (v / 1000000).toFixed(1)) + "m"
    );
  if (v >= 1000) return s + Math.round(v / 1000) + "k";
  return s + v;
};

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// mode: undefined → "14 Aug 2018" · "short" → "Aug 18" · "year" → "2018"
export const formatDate = (unixSeconds, mode) => {
  const d = new Date(unixSeconds * 1000);
  const mon = MONTHS[d.getUTCMonth()];
  if (mode === "short") return mon + " " + String(d.getUTCFullYear()).slice(2);
  if (mode === "year") return String(d.getUTCFullYear());
  return d.getUTCDate() + " " + mon + " " + d.getUTCFullYear();
};

// Y-axis step: max/4 rounded up to the next 1/2/2.5/5/10 × power of ten.
export const niceStep = (max) => {
  const raw = max / 4;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const cands = [1, 2, 2.5, 5, 10].map((m) => m * pow);
  for (let i = 0; i < cands.length; i += 1) if (cands[i] >= raw) return cands[i];
  return cands[cands.length - 1];
};

// Defensive: keep only well-formed entries, sort ascending by time.
export const normalizeHistory = (payload) => {
  const list = Array.isArray(payload && payload.history) ? payload.history : [];
  return list
    .filter(
      (e) =>
        e && typeof e.time === "number" && typeof e.value === "number"
    )
    .slice()
    .sort((a, b) => a.time - b.time);
};

// Range chips 1Y/3Y/5Y/All — cutoff measured back from the LAST entry.
export const filterByRange = (series, range) => {
  const years = { "1Y": 1, "3Y": 3, "5Y": 5 }[range];
  if (!years || series.length === 0) return series;
  const cutoff = series[series.length - 1].time - years * 365.25 * 86400;
  return series.filter((p) => p.time >= cutoff);
};

// Header summary. direction: "first" (single entry) | "up" | "down" | "flat".
// Delta is always vs. the previous entry (decided default, caption says so).
export const computeSummary = (series) => {
  if (!series.length) return null;
  const last = series[series.length - 1];
  const prev = series.length > 1 ? series[series.length - 2] : null;
  let peak = series[0];
  series.forEach((p) => {
    if (p.value > peak.value) peak = p;
  });
  const deltaValue = prev ? last.value - prev.value : 0;
  const deltaPct = prev && prev.value ? (deltaValue / prev.value) * 100 : 0;
  const direction = !prev
    ? "first"
    : deltaValue > 0
      ? "up"
      : deltaValue < 0
        ? "down"
        : "flat";
  return { last, prev, first: series[0], peak, deltaValue, deltaPct, direction };
};

// Table rows, newest first. delta/pct vs. the next-older entry; null for the
// oldest row (no reference), pct null when the older value is 0.
export const buildRows = (series) => {
  const desc = series.slice().reverse();
  return desc.map((p, i) => {
    const older = desc[i + 1] || null;
    const delta = older ? p.value - older.value : null;
    const pct =
      older && older.value ? ((p.value - older.value) / older.value) * 100 : null;
    return { entry: p, delta, pct };
  });
};
